/* Beneath-the-footer reveal (sb-footer-reveal.liquid).
   The band sits position:fixed at z-index:-1 behind the page; an in-flow
   spacer at the very end of the document opens a window onto it, so scrolling
   past the footer lifts the page like a curtain off the hidden message
   (the seed.com overscroll easter egg).

   This script:
   - arms the effect only when the page above the spacer overfills the
     viewport — on shorter pages the band would peek out below the footer
     instead of hiding behind it;
   - keeps the band visibility-hidden unless the spacer window is actually
     on screen (raw scroll truth, never the eased value): the fixed layer
     otherwise bleeds through transparent sections mid-page — an eased
     visibility linger did exactly that through the pumpkin gate's frosted
     panel (owner report 2026-07-22). When the window closes, the settle
     snaps to 0 so the next approach re-forms from scratch;
   - drives --sb-frev-p (formation fraction, 0→1) for the scroll-scrubbed
     settle of the type block and photo (CSS-side, reduced-motion gated).
     The published value spans the first FORM_SPAN of the window and, while
     FORMING, chases its target at a capped rate (FORM_RATE, soft landing
     via LAND_TAU) — liquid inertia. A rate cap, not a low-pass: an
     exponential chase spends its speed on the early fade-in and crawls
     through the tail, so the liquid middle (drops fusing, crystallization)
     still snapped (owner report 2026-07-22). Constant rate gives every
     phase equal time — a flick plays the whole formation evenly over
     ~5.5s — while scrolling slower than the cap stays 1:1, as does the
     physical curtain (real scroll occlusion) always. MELTING back is
     1:1, no inertia: scrolling up re-melts the type in lockstep with the
     footer sliding over it (owner, 2026-07-23);
   - liquid type: the headline renders through a CSS blur() and then the
     #sb-frev-goo filter (alpha threshold only), and this script scrubs the
     blur down as the formation progresses by writing --sb-frev-goo on the
     h2. The type itself is static and always at full opacity — legible
     from the first uncovered pixel, molten (threshold eats hairlines/
     serifs, letters fuse at contact) and sharpening to crisp as the
     curtain lifts. Two earlier choreographies retired 2026-07-23 by owner
     report: per-word rise/scatter at GOO_RATIO 0.14 left the type below
     the threshold's alpha cut until half-cleared ("fades in 1-2s late"),
     and covering pill drop-bodies read as slow white slabs. Reduced motion
     (or a missing headline) skips the filter entirely — the band shows the
     plain type.
     Until 2026-08-31 the blur lived INSIDE the SVG filter and this script
     rewrote feGaussianBlur's stdDeviation every frame. Mutating a filter
     primitive invalidates the filter resource for every client (WebKit
     also marks the client for layout: RenderSVGResourceFilter →
     markForLayoutAndParentResourceInvalidation); a filter-property change
     on the h2 is only a repaint of the h2, in every engine. The bigger
     cost, fixed the same day, was the photo's zoom (owner: "3 frames per
     second"): without a compositor layer of its own, each --sb-frev-p
     tick re-rasterised the whole viewport through the photo filter and
     the colour-blend tint — 28fps with 60ms frames at 2560×1440@2x under
     software rendering in Chromium (GPU-backed Brave and WebKit held 60
     on an M2 Pro), 53fps once the scene rides its own layer, 120fps with
     the zoom off entirely. See the scene rules in the section CSS. */
(() => {
  'use strict';

  /* Blur = ratio × title font-size. The goo threshold (feColorMatrix
     alpha ×20−10) erases blurred alpha under 0.5, and a stroke of width w
     survives σ only while erf(w/(2√2σ)) > 0.5, i.e. σ ≲ w/1.35. Source
     Serif stems run ≈0.08em, so the ratio must stay ≲0.055 for the
     sentence to be READABLE at full melt — bigger ratios erase the type
     (the 2026-07-23 "fades in late" bug). 0.05 keeps stems solid while
     hairlines and serifs melt away: liquid but legible. */
  const GOO_RATIO = 0.05;
  const MELT_FROM = 0; /* the goo starts collapsing with the very first scroll — the sentence is
     present, already clearing, from the beginning (owner, 2026-07-23) */
  const SOLID_AT = 0.93; /* p where the filter comes off — crisp serif */
  const FORM_RATE = 0.18; /* progress/s cap — full formation ≈ 1/FORM_RATE seconds (owner: slower still ×2, 2026-07-23) */
  const LAND_TAU = 0.25; /* s — deceleration window for the soft landing at the target */
  const FORM_SPAN = 0.55; /* window fraction that plays the whole formation — the sentence is settled
     by half-open; crystallization never needs the absolute scroll bottom held
     (owner: mantra fades in way too late, 2026-07-23) */

  function init() {
    const root = document.querySelector('[data-sb-frev]');
    if (!root || root.dataset.sbFrevReady === '1') return;
    root.dataset.sbFrevReady = '1';

    const spacer = root.querySelector('[data-sb-frev-spacer]');
    const band = root.querySelector('[data-sb-frev-band]');
    if (!spacer || !band) return;

    const title = band.querySelector('[data-sb-frev-title]');
    const gooDef = root.querySelector('[data-sb-frev-goo]');
    const liquid =
      !!(title && gooDef) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (liquid) title.classList.add('is-liquid');

    let raf = 0;
    let growth = null; /* ResizeObserver on the document flow — see below */
    let lastGoo = '';
    let gooPx = 0; /* GOO_RATIO × rendered title px; 0 = re-measure (init/resize) */
    let disp = null; /* displayed (eased) progress; null = adopt raw on next frame */
    let lastT = 0;

    const update = (now) => {
      raf = 0;
      if (!root.isConnected) {
        /* Section was re-rendered (theme editor) — retire this instance. */
        window.removeEventListener('scroll', queue);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('load', queue);
        if (growth) growth.disconnect();
        return;
      }
      const vh = window.innerHeight;
      const armed = document.documentElement.scrollHeight - spacer.offsetHeight > vh + 1;
      root.classList.toggle('is-armed', armed);
      if (!armed) {
        band.classList.remove('is-open');
        disp = null;
        lastT = 0;
        return;
      }
      const rect = spacer.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (vh - rect.top) / Math.max(rect.height, 1)));

      /* Window closed (spacer fully below the viewport) — the page occludes
         the band. Hide on this raw truth alone and snap the settle: the
         eased value must never keep the fixed layer visible mid-page. */
      if (p <= 0) {
        band.classList.remove('is-open');
        band.style.setProperty('--sb-frev-p', '0');
        disp = 0;
        lastT = 0;
        return;
      }

      /* The formation plays over the first FORM_SPAN of the window: the
         target hits 1 while the curtain is still lifting, so the settled
         sentence greets the visitor instead of trailing the hard bottom. */
      const target = Math.min(1, p / FORM_SPAN);

      /* Liquid inertia — one-way. Forming (target above disp) chases at a
         capped rate with a soft landing, frame-rate independent. Melting
         back (target below disp) adopts the target outright: the blob must
         visibly reverse in lockstep with the footer sliding back over it
         (owner, 2026-07-23) — inertia there just left solid type until the
         page swallowed it. First frame after load/arm adopts the target so
         a restored scroll position doesn't replay the formation. */
      const dt = Math.min(0.1, lastT ? (now - lastT) / 1000 : 0);
      lastT = now;
      if (disp === null || target < disp) {
        disp = target;
      } else if (disp !== target) {
        const gap = target - disp;
        const v = Math.min(FORM_RATE, gap / LAND_TAU);
        disp += v * dt;
        if (target - disp < 0.0005) {
          disp = target;
          lastT = 0;
        } else {
          queue(); /* keep settling after the scroll stops */
        }
      }

      band.classList.add('is-open');
      band.style.setProperty('--sb-frev-p', disp.toFixed(4));

      if (liquid) {
        const solid = disp >= SOLID_AT;
        title.classList.toggle('is-solid', solid);
        if (!solid) {
          if (!gooPx) gooPx = GOO_RATIO * (parseFloat(getComputedStyle(title).fontSize) || 40);
          const t = Math.min(1, Math.max(0, (disp - MELT_FROM) / (SOLID_AT - MELT_FROM)));
          const melt = 1 - t * t * (3 - 2 * t); /* smoothstep, eased both ends */
          const goo = (gooPx * melt).toFixed(2);
          if (goo !== lastGoo) {
            lastGoo = goo;
            /* On the h2 itself, never the SVG primitive — see the header. */
            title.style.setProperty('--sb-frev-goo', goo + 'px');
          }
        }
      }
    };

    const queue = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    const onResize = () => {
      gooPx = 0; /* the clamp may have landed on a new size */
      lastGoo = '';
      queue();
    };

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', onResize);

    /* The document keeps growing after the first frame — lazy images,
       deferred sections, web fonts. The very first update therefore runs
       against a page that is still short, where the spacer can sit inside
       the viewport: the band opens, and because nothing but a scroll or a
       resize re-runs the update, that stale is-open survives on a page the
       visitor has not scrolled at all — the fixed layer then bleeds green
       through the transparent header strip at the top (owner report
       2026-09-08, pumpkin PDP on desktop). Re-measure whenever the flow
       changes height, and once more at load. Convergent, not a loop: the
       only height this update itself writes is the spacer's, and the armed
       state it comes from is settled by then. */
    window.addEventListener('load', queue);
    if ('ResizeObserver' in window) {
      growth = new ResizeObserver(queue);
      growth.observe(document.body);
    }

    queue();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
